/**
 * @file SeoAuditTab.tsx
 * @description Вкладка «Проверка сайта» раздела «Сайт → SEO» (phase19.16,
 * партия Р3в, v0.19.64; SB132, SEO12; uiux-r3 §3.5): «проверено 19.10 14:50
 * [Проверить снова]», предупреждения (!) — первой «Сайт закрыт от
 * поисковиков» (полоса — над кабинетом, здесь не дублируется, QA Р3в),
 * сведения (ⓘ) и строки «всё хорошо» (✓). Все замечания —
 * предупреждения, не блокировки. Состояния: загрузка, «Проверить не удалось —
 * сайт работает, попробуйте позже [Повторить]», «Замечаний нет».
 */

import { CheckCircle2, RefreshCw } from "lucide-react";

import { Button } from "../../ui/button";
import { ListError } from "../../ui/ListStates";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { Skeleton } from "../../ui/skeleton";
import { AuditGroupCard } from "./AuditGroupCard";
import type { SeoAuditApi } from "../../../hooks/site-seo/useSeoAudit";
import { formatShopDateTime } from "../../../lib/shop-time";
import { auditLayout } from "../../../lib/site-seo/audit-view";
import { useCurrentContext } from "../../../auth/hooks";

interface Props {
  api: SeoAuditApi;
}

export function SeoAuditTab({ api }: Props) {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const { audit } = api;
  if (api.failed && !audit) return <ListError message="Проверить не удалось — сайт работает, попробуйте позже." onRetry={() => void api.run(true)} />;
  if (!audit) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true" aria-label="Проверяем сайт">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    );
  }
  const layout = auditLayout(audit, slug);
  return (
    <div className="flex min-w-0 flex-col gap-4" aria-busy={api.loading}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="type-body text-on-surface-variant">Проверено {formatShopDateTime(audit.checkedAt)}</span>
        <Button variant="outline" className="max-sm:w-full" loading={api.loading} onClick={() => void api.run(true)}>
          <RefreshCw aria-hidden />
          Проверить снова
        </Button>
      </div>
      {api.failed ? <NoticeStrip tone="warning">Проверить заново не удалось — показан прошлый результат. Сайт работает, попробуйте позже.</NoticeStrip> : null}
      {layout.warnings.map((group) => (
        <AuditGroupCard key={group.code} group={group} />
      ))}
      {layout.infos.map((group) => (
        <AuditGroupCard key={group.code} group={group} />
      ))}
      <ul className="flex flex-col gap-1">
        {layout.clean ? (
          <li className="type-body flex items-center gap-2 text-success">
            <CheckCircle2 aria-hidden className="size-4 shrink-0" />
            Замечаний нет
          </li>
        ) : null}
        {layout.ok.map((line) => (
          <li key={line} className="type-body flex items-center gap-2 text-on-surface">
            <CheckCircle2 aria-hidden className="size-4 shrink-0 text-success" />
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}
